import axios from "axios";

// Enterprise Loyalty API Service for mtier endpoints
const MTIER_BASE_URL = process.env.REACT_APP_MULTI_LEVEL_BASE_URL || "https://mtier0.loystar.co/";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api0.loystar.co/api/v2/";
const UGIFTME_BASE_URL = process.env.REACT_APP_UGM_BASE_URL || "https://api-stage.ugift.me/";

class EnterpriseLoyaltyAPI {
  constructor() {
    this.mtierClient = axios.create({
      baseURL: MTIER_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.ugiftmeClient = axios.create({
      baseURL: UGIFTME_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add token interceptor for mtier requests
    this.mtierClient.interceptors.request.use((config) => {
      const token = localStorage.getItem("enterprise-auth-token");
      const storedClient = localStorage.getItem("enterprise-client");
      const storedUid = localStorage.getItem("enterprise-uid");
      const storedExpiry = localStorage.getItem("enterprise-expiry");
      const mtierToken = localStorage.getItem("enterprise-mtier-token");
      const orgToken = localStorage.getItem("enterprise-org-token");
      const user = this.getCurrentUser();
      
      // Do not send old headers during login to prevent 431 Request Header Fields Too Large
      if (config.url && (
        config.url.includes("auth/login") || 
        config.url.includes("auth/organisation_login") || 
        config.url.includes("auth/organisation/2fa/login")
      )) {
        return config;
      }
      
      // For UGiftMe endpoints, use organization token
      if (config.url && config.url.includes("auth/ugiftme")) {
        console.log("🔐 UGiftMe endpoint detected:", config.url);
        console.log("📝 Org token from localStorage:", orgToken ? `${orgToken.substring(0, 10)}...` : "NULL/UNDEFINED");
        if (orgToken) {
          config.headers["Authorization"] = `Bearer ${orgToken}`;
          console.log("✅ Authorization header set for UGiftMe");
        } else {
          console.error("❌ No org token available for UGiftMe endpoint!");
        }
      }
      // For GraphQL endpoints, use specific headers (HYPHEN format - backend requires it!)
      else if (config.url === "graphql") {
        if (token) {
          config.headers["access-token"] = token;  // Use HYPHEN not underscore
        }
        if (storedClient) {
          config.headers["client"] = storedClient;
        }
        if (storedUid) {
          config.headers["uid"] = storedUid;
        } else if (user?.email) {
          config.headers["uid"] = user.email;
        }
        if (storedExpiry) {
          config.headers["expiry"] = storedExpiry;
        }
        // Add Bearer token for GraphQL authorization
        if (mtierToken) {
          config.headers["authorization"] = `Bearer ${mtierToken}`;
        }
      } else {
        // For other REST endpoints, use access-token headers
        if (token) {
          config.headers["access-token"] = token;
        }
        if (storedClient) {
          config.headers["client"] = storedClient;
        }
        if (storedUid) {
          config.headers["uid"] = storedUid;
        } else if (user?.email) {
          config.headers["uid"] = user.email;
        }
        if (storedExpiry) {
          config.headers["expiry"] = storedExpiry;
        }
        config.headers["token-type"] = "Bearer";
      }
      
      return config;
    });

    // Add token interceptor for REST API requests
    this.apiClient.interceptors.request.use((config) => {
      const token = localStorage.getItem("enterprise-auth-token");
      const storedClient = localStorage.getItem("enterprise-client");
      const storedUid = localStorage.getItem("enterprise-uid");
      const storedExpiry = localStorage.getItem("enterprise-expiry");
      
      if (token) {
        config.headers["access-token"] = token;
      }
      if (storedClient) {
        config.headers["client"] = storedClient;
      }
      if (storedUid) {
        config.headers["uid"] = storedUid;
      }
      if (storedExpiry) {
        config.headers["expiry"] = storedExpiry;
      }
      config.headers["token-type"] = "Bearer";
      
      return config;
    });


  }

  // ==================== AUTHENTICATION ====================
  
  /**
   * Enterprise organization login
   * Performs dual login: organization first, then merchant to get GraphQL auth headers
   * @param {string} email - Organization admin email
   * @param {string} password - Password
   * @returns {Promise} Login response with token
   */
  async organizationLogin(email, password) {
    try {
      console.log('Login attempt for:', email);
      
      // Step 1: Organization login
      let orgResponse;
      try {
        orgResponse = await this.mtierClient.post("auth/organisation_login", {
          email,
          password,
        });
      } catch (error) {
        console.warn('Organization login error response:', error.response?.data);
        const errorData = error.response?.data;
        const nestedData = errorData?.data || errorData;
        
        if (nestedData?.two_factor_required) {
          console.log('2FA required from error response');
          return nestedData;
        }
        throw error;
      }

      const responseData = orgResponse.data;
      const effectiveData = responseData?.data || responseData;
      
      console.log('Organization login response data:', effectiveData);

      // Check for 2FA requirement
      if (effectiveData?.two_factor_required) {
        console.log('2FA required from successful response');
        return effectiveData;
      }

      if (!effectiveData?.token && !effectiveData?.organisation) {
        throw new Error("Invalid organization login response format");
      }
      
      // Step 2: Merchant login to get GraphQL auth headers
      const merchantResponse = await this.mtierClient.post("auth/login", {
        email,
        password,
      });
      
      const merchantBody = merchantResponse.data;
      const effectiveMerchant = merchantBody?.data || merchantBody;
      const authHeaders = merchantBody?.headers || {};
      
      console.log('Merchant login response:', effectiveMerchant);

      // Store GraphQL auth credentials
      const token = authHeaders['access-token'] || effectiveMerchant['access_token'] || effectiveMerchant['token'];
      const client = authHeaders['client'] || effectiveMerchant['client'];
      const uid = authHeaders['uid'] || effectiveMerchant['uid'] || effectiveMerchant['email'];
      const mtierToken = effectiveMerchant.mtierToken || effectiveMerchant.token;

      if (token) localStorage.setItem("enterprise-auth-token", token);
      if (client) localStorage.setItem("enterprise-client", client);
      if (uid) localStorage.setItem("enterprise-uid", uid);
      if (authHeaders['expiry']) localStorage.setItem("enterprise-expiry", authHeaders['expiry']);
      if (mtierToken) localStorage.setItem("enterprise-mtier-token", mtierToken);
      
      // Store merchant ID from auth/login response (for POS transactions)
      const merchantId = effectiveMerchant.merchant_id || 
                        effectiveMerchant.id || 
                        effectiveData.organisation?.merchant_id ||
                        effectiveData.merchant_id;
      
      if (merchantId) {
        localStorage.setItem("enterprise-merchant-id", merchantId.toString());
      }
      
      // Store user/merchant data
      localStorage.setItem("enterprise-user", JSON.stringify(effectiveMerchant));
      
      // Store organization data and token
      localStorage.setItem("enterprise-organization", JSON.stringify(effectiveData.organisation));
      localStorage.setItem("enterprise-org-token", effectiveData.token);

      return {
        ...effectiveData,
        merchant: effectiveMerchant,
      };
    } catch (error) {
      console.error('Unified login failure:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Complete 2FA login
   * @param {string} email - Email
   * @param {string} password - Password
   * @param {string} token - 6-digit 2FA token
   */
  async login2FA(email, password, token) {
    try {
      console.log('2FA Login attempt for:', email);
      const response = await this.mtierClient.post("auth/organisation/2fa/login", {
        email,
        token,
      });

      const responseBody = response.data;
      const data = responseBody?.data || responseBody;

      // Handle cases where API returns 200 but includes an error map
      if (data?.status === 'error' || data?.status === false || data?.success === false || data?.error) {
        throw new Error(data.message || data.error || "Invalid token");
      }

      if (!data?.token) {
        throw new Error(data?.message || "Invalid 2FA token. Please check that you entered the right code.");
      }

      if (data.token) {
        localStorage.setItem("enterprise-org-token", data.token);
        localStorage.setItem("enterprise-organization", JSON.stringify(data.organisation));
        
        // Ensure the full merchant scope is synchronized since the 2FA response tends to miss the auth headers
        try {
          console.log('Synchronizing merchant credentials following 2FA...');
          const merchantResponse = await this.mtierClient.post("auth/login", {
            email,
            password,
          });
          
          const merchantBody = merchantResponse.data;
          const effectiveMerchant = merchantBody?.data || merchantBody;
          const authHeaders = merchantBody?.headers || {};
          
          const accessToken = authHeaders['access-token'] || effectiveMerchant['access_token'] || effectiveMerchant['token'];
          const client = authHeaders['client'] || effectiveMerchant['client'];
          const uid = authHeaders['uid'] || effectiveMerchant['uid'] || effectiveMerchant['email'];
          const mtierToken = effectiveMerchant.mtierToken || effectiveMerchant.token;

          if (accessToken) localStorage.setItem("enterprise-auth-token", accessToken);
          if (client) localStorage.setItem("enterprise-client", client);
          if (uid) localStorage.setItem("enterprise-uid", uid);
          if (authHeaders['expiry']) localStorage.setItem("enterprise-expiry", authHeaders['expiry']);
          if (mtierToken) localStorage.setItem("enterprise-mtier-token", mtierToken);
          
          const merchantId = effectiveMerchant.merchant_id || 
                            effectiveMerchant.id || 
                            data.organisation?.merchant_id ||
                            data.merchant_id;
          
          if (merchantId) {
            localStorage.setItem("enterprise-merchant-id", merchantId.toString());
          }
          
          localStorage.setItem("enterprise-user", JSON.stringify(effectiveMerchant));
          
          // Embed the full merchant record into the 2FA consequence 
          data.merchant = effectiveMerchant;
        } catch (merchantError) {
          console.warn('Failed to fully synchronize merchant credentials post 2FA:', merchantError);
        }
      }

      return data;
    } catch (error) {
      console.error('2FA Login failed:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Initiate 2FA setup
   * @param {number} userId - User ID
   */
  async enable2FA(userId) {
    try {
      const response = await this.mtierClient.post("auth/organisation/2fa/enable", {
        userId,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify and activate 2FA
   * @param {number} userId - User ID
   * @param {string} token - 6-digit token
   */
  async verify2FAActivation(userId, token) {
    try {
      const response = await this.mtierClient.post("auth/organisation/2fa/verify-activation", {
        userId,
        token,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Disable 2FA
   * @param {number} userId - User ID
   * @param {string} token - 6-digit token
   */
  async disable2FA(userId, token) {
    try {
      const response = await this.mtierClient.post("auth/organisation/2fa/disable", {
        userId,
        token,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new organization account
   * @param {Object} organizationData - Organization registration data
   * @returns {Promise} Registration response
   */
  async createOrganization(organizationData) {
    try {
      const response = await this.mtierClient.post("auth/add_organisation", organizationData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a merchant account on Rails API
   * @param {Object} merchantData - Merchant registration data
   * @returns {Promise} Registration response
   */
  async createMerchantAccount(merchantData) {
    try {
      const response = await this.apiClient.post("auth", merchantData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Try to login with merchant credentials (to check if merchant account exists)
   * @param {string} email - Email
   * @param {string} password - Password
   * @returns {Promise} Login response or null if account doesn't exist
   */
  async tryMerchantLogin(email, password) {
    try {
      const response = await this.mtierClient.post("auth/login", {
        email,
        password,
      });
      return response.data;
    } catch (error) {
      // Return null if account doesn't exist (instead of throwing)
      if (error.response?.status === 401 || error.response?.status === 404) {
        return null;
      }
      throw this.handleError(error);
    }
  }

  /**
   * Upload an image file
   * @param {File} file - Image file to upload
   * @returns {Promise<string>} URL of uploaded image
   */
  async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.mtierClient.post("upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Return the URL from the response
      return response.data.url || response.data.data?.url || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Logout enterprise user
   */
  logout() {
    localStorage.removeItem("enterprise-auth-token");
    localStorage.removeItem("enterprise-user");
    localStorage.removeItem("enterprise-organization");
    localStorage.removeItem("enterprise-org-token");
    localStorage.removeItem("enterprise-client");
    localStorage.removeItem("enterprise-uid");
    localStorage.removeItem("enterprise-expiry");
    localStorage.removeItem("enterprise-mtier-token");
    localStorage.removeItem("enterprise-merchant-id");
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem("enterprise-auth-token");
  }

  /**
   * Get current enterprise user
   */
  getCurrentUser() {
    const userStr = localStorage.getItem("enterprise-user");
    if (!userStr || userStr === "undefined" || userStr === "null") {
      return null;
    }
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Error parsing enterprise user:", error);
      return null;
    }
  }

  /**
   * Get current organization
   */
  getCurrentOrganization() {
    const orgStr = localStorage.getItem("enterprise-organization");
    if (!orgStr || orgStr === "undefined" || orgStr === "null") {
      return null;
    }
    try {
      return JSON.parse(orgStr);
    } catch (error) {
      console.error("Error parsing enterprise organization:", error);
      return null;
    }
  }

  /**
   * Get POS merchant ID from auth/login response
   */
  getPosMerchantId() {
    return localStorage.getItem("enterprise-merchant-id");
  }

  // ==================== ORGANIZATION MANAGEMENT ====================

  /**
   * Get organization details
   * @param {number} organisationId - Organization ID
   * @returns {Promise<Object>} Organization details
   */
  async getOrganisation(organisationId) {
    try {
      const query = `
        query GetOrganisation($organisation_id: Int!) {
          getOrganisation(organisation_id: $organisation_id) {
            message
            organisationDetails {
              organisation_name
              organisation_phone_number
              organisation_address
              organisation_email
              organisation_category
              organisation_currency
              organisation_country
              organisation_state
              organisation_id
              organisation_banner
              organisation_logo
              custom_id_label
              custom_id_enabled
              organisation_design {
                backgroundColor
                navColor
                brandColor
                organisation_id
                fontColor
              }
            }
          }
        }
      `;
      const data = await this.graphqlQuery(query, { organisation_id: parseInt(organisationId) });
      return data.getOrganisation.organisationDetails;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update organization profile using GraphQL mutation
   * @param {Object} profileData - Organization profile data
   * @returns {Promise<Object>} Updated organization details
   */
  async updateOrganizationProfile(profileData) {
    try {
      console.log('Updating organization profile (original):', profileData);
      
      // Map inconsistent field names to match GraphQL updateOrganisationInput type
      const mappedData = {
        ...profileData,
        country: profileData.organisation_country,
        state: profileData.organisation_state,
        currency: profileData.organisation_currency
      };
      
      // Remove the old prefixed names to avoid GraphQL errors
      delete mappedData.organisation_country;
      delete mappedData.organisation_state;
      delete mappedData.organisation_currency;
      
      console.log('Updating organization profile (mapped):', mappedData);
      
      const mutation = `
        mutation UpdateOrganisation($data: updateOrganisationInput!) {
          updateOrganisationDetails(data: $data) {
            message
            organisationDetails {
              organisation_name
              organisation_email
              organisation_phone_number
              organisation_address
              organisation_logo
              organisation_banner
              organisation_category
              organisation_currency
              organisation_country
              organisation_state
              organisation_id
              organisation_design {
                backgroundColor
                navColor
                brandColor
                fontColor
                organisation_id
              }
            }
          }
        }
      `;
      
      const result = await this.graphqlQuery(mutation, { data: mappedData });
      console.log('Profile update response:', result);
      
      return result.updateOrganisationDetails.organisationDetails;
    } catch (error) {
      console.error('Profile update error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get merchant information including UUID
   * @returns {Promise<Object>} Merchant data with UUID
   */
  async getMerchant() {
    try {
      const query = `
        query GetMerchant {
          getMerchant {
            id
          }
        }
      `;
      const data = await this.graphqlQuery(query);
      return data.getMerchant;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== B2B STAFF ID INTEGRATION ====================

  /**
   * Update Organisation Custom ID Configuration
   */
  async updateCustomIdConfig(organisationId, config) {
    try {
      const response = await this.mtierClient.post("auth/organisation/custom-id/config", {
        organisation_id: organisationId,
        ...config
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk upload whitelist members
   */
  async bulkUploadCustomIdMembers(organisationId, members) {
    try {
      const response = await this.mtierClient.post("auth/organisation/custom-id/bulk-upload", {
        organisation_id: organisationId,
        members
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update the merchant's URewards ID (slug)
   * @param {string} uRewardsId - The new URewards ID/slug
   * @returns {Promise<Object>} Result with message and uRewardsId
   */
  async updateURewardsId(uRewardsId) {
    try {
      const user = this.getCurrentUser() || {};
      const merchantId = parseInt(
        this.getPosMerchantId() || user?.merchant_id || user?.id,
        10
      );

      if (!merchantId) throw new Error("Merchant ID not found. Please log in again.");

      const query = `
        query updateMerchantSenderId($uRewardsId: String, $merchant_id: Int) {
          updateMerchantSenderId(uRewardsId: $uRewardsId, merchant_id: $merchant_id) {
            message
            uRewardsId
          }
        }
      `;

      const data = await this.graphqlQuery(query, { uRewardsId, merchant_id: merchantId });
      return data?.updateMerchantSenderId;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ENGAGEMENT CHANNELS ====================

  /**
   * Fetch the merchant's URewards ID (slug) via GraphQL.
   * This is the unique identifier used in customer-facing URLs (e.g. /sterling).
   * @returns {Promise<string|null>} The URewards ID or null if not set
   */
  async getURewardsId() {
    try {
      const user = this.getCurrentUser() || {};
      const merchantId = parseInt(
        this.getPosMerchantId() || user?.merchant_id || user?.id,
        10
      );

      if (!merchantId) return null;

      const query = `
        query getMerchantSenderId($merchant_id: Int) {
          getMerchantSenderId(merchant_id: $merchant_id) {
            message
            uRewardsId
          }
        }
      `;

      const data = await this.graphqlQuery(query, { merchant_id: merchantId });
      return data?.getMerchantSenderId?.uRewardsId || null;
    } catch (error) {
      console.warn("Unable to fetch URewards ID:", error);
      return null;
    }
  }

  /**
   * Get customer engagement channels payload for enterprise dashboard.
   * URLs follow the format: {baseUrl}/{uRewardsId} which the customer platform resolves.
   * @returns {Promise<Object>} Channel payload with embed code, direct link, QR value and social links
   */
  async getEngagementChannelsPayload() {
    try {
      const organization = this.getCurrentOrganization() || {};
      const user = this.getCurrentUser() || {};
      const merchant = await this.getMerchant().catch(() => null);

      const merchantId =
        merchant?.id ||
        this.getPosMerchantId() ||
        user?.merchant_id ||
        user?.id ||
        "";

      const organisationId =
        organization?.organisation_id ||
        organization?.id ||
        user?.organisation_id ||
        user?.organization_id ||
        "";

      const organizationName =
        organization?.organisation_name ||
        organization?.name ||
        "Your organization";

      // Fetch the merchant's URewards slug (e.g. "sterling")
      const uRewardsId = await this.getURewardsId();

      const baseUrl = (
        process.env.REACT_APP_LOYALTY_EMBED_BASE_URL ||
        process.env.REACT_APP_UREWARDS_BASE_URL ||
        "https://loystar.shop"
      ).replace(/\/+$/, "");

      const directShareUrl = uRewardsId ? `${baseUrl}/${uRewardsId}` : "";
      const embedUrl = uRewardsId ? `${baseUrl}/${uRewardsId}?embed=true` : "";
      const shareMessage = uRewardsId
        ? `Explore ${organizationName}'s loyalty rewards and curated gift cards: ${directShareUrl}`
        : "";

      const embedCode = embedUrl
        ? `<iframe src="${embedUrl}" title="Loyalty Rewards" width="100%" height="720" style="border:0;max-width:1200px;margin:20px 0;" loading="lazy"></iframe>`
        : "";

      let curatedGiftCardsCount = 0;
      try {
        if (merchantId) {
          const curatedProductsResponse = await this.getCuratedProducts(merchantId, {
            is_active: true,
            page: 1,
            limit: 1,
          });

          curatedGiftCardsCount =
            curatedProductsResponse?.data?.pagination?.total ||
            curatedProductsResponse?.data?.total ||
            curatedProductsResponse?.data?.meta?.total ||
            curatedProductsResponse?.data?.curations?.length ||
            0;
        }
      } catch (curationError) {
        console.warn("Unable to load curated gift cards count:", curationError);
      }

      return {
        success: true,
        data: {
          organizationName,
          merchantId,
          organisationId,
          uRewardsId: uRewardsId || "",
          hasRequiredIdentifiers: !!uRewardsId,
          directShareUrl,
          embedUrl,
          embedCode,
          qrCodeValue: directShareUrl,
          shareMessage,
          curatedGiftCardsCount,
          shareLinks: {
            whatsapp: directShareUrl ? `https://wa.me/?text=${encodeURIComponent(shareMessage)}` : "",
            email: directShareUrl ? `mailto:?subject=${encodeURIComponent("Loyalty rewards and gift cards")}&body=${encodeURIComponent(shareMessage)}` : "",
            sms: directShareUrl ? `sms:?&body=${encodeURIComponent(shareMessage)}` : "",
          },
          generatedAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch authenticated widget data from /api/v1/my-widgets.
   * Returns the full widget payload including all 4 layouts, theme params,
   * share links, and merchant/org metadata.
   * @returns {Promise<Object>} Widget data from the backend
   */
  async getMyWidgets() {
    try {
      const response = await this.mtierClient.get("api/v1/my-widgets");
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Build a customized embed URL for a given widget layout with theme overrides.
   * @param {Object} widget - Widget object from the API (has layout, embed_url)
   * @param {Object} theme - Theme overrides (primary_color, bg, radius, etc.)
   * @returns {string} The customized embed URL
   */
  buildCustomEmbedUrl(widget, theme = {}) {
    if (!widget?.embed_url) return "";
    try {
      const url = new URL(widget.embed_url);
      Object.entries(theme).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          url.searchParams.set(key, value);
        }
      });
      return url.toString();
    } catch {
      // If embed_url is relative, build manually
      const baseUrl = (
        process.env.REACT_APP_LOYALTY_EMBED_BASE_URL ||
        process.env.REACT_APP_UREWARDS_BASE_URL ||
        "https://loystar.shop"
      ).replace(/\/+$/, "");
      const sep = widget.embed_url.includes("?") ? "&" : "?";
      const themeParams = Object.entries(theme)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
      return `${baseUrl}${widget.embed_url}${themeParams ? `${sep}${themeParams}` : ""}`;
    }
  }

  /**
   * Build an iframe embed code snippet for a widget with optional theme overrides.
   * @param {Object} widget - Widget object from the API
   * @param {Object} theme - Theme overrides
   * @returns {string} HTML iframe snippet
   */
  buildEmbedCode(widget, theme = {}) {
    const url = this.buildCustomEmbedUrl(widget, theme);
    if (!url) return "";
    const height = widget?.iframe_height || 720;
    const transition = (widget?.layout === "button" || widget?.layout === "badge")
      ? "transition:height 0.3s ease;" : "";
    return `<iframe src="${url}" title="Loyalty Rewards" width="100%" height="${height}" style="border:0;max-width:100%;${transition}" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>`;
  }

  // ==================== LOYALTY ANALYTICS ====================

  /**
   * Get dashboard summary with key metrics
   * @param {string} merchantId - Merchant ID
   * @param {string} period - Time period: 7d, 30d, 90d, 1y (default: 30d)
   */
  async getDashboardSummary(merchantId, period = '30d') {
    try {
      const response = await this.mtierClient.get("auth/analytics/dashboard", {
        params: { merchant_id: merchantId, period }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get new loyalty members with period-over-period growth
   * @param {string} merchantId - Merchant ID
   * @param {string} period - Time period: 7d, 30d, 90d, 1y (default: 30d)
   */
  async getNewMembers(merchantId, period = '30d') {
    try {
      const response = await this.mtierClient.get("auth/analytics/new-members", {
        params: { merchant_id: merchantId, period }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get retention rate with comparison to previous period
   * @param {string} merchantId - Merchant ID
   * @param {string} period - Time period: 7d, 30d, 90d, 1y (default: 30d)
   */
  async getRetentionRate(merchantId, period = '30d') {
    try {
      const response = await this.mtierClient.get("auth/analytics/retention", {
        params: { merchant_id: merchantId, period }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get active users (users with point transactions)
   * @param {string} merchantId - Merchant ID
   * @param {string} period - Time period: 7d, 30d, 90d, 1y (default: 30d)
   */
  async getActiveUsers(merchantId, period = '30d') {
    try {
      const response = await this.mtierClient.get("auth/analytics/active-users", {
        params: { merchant_id: merchantId, period }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get redemption rate
   * @param {string} merchantId - Merchant ID
   * @param {string} period - Time period: 7d, 30d, 90d, 1y (default: 30d)
   */
  async getRedemptionRate(merchantId, period = '30d') {
    try {
      const response = await this.mtierClient.get("auth/analytics/redemption-rate", {
        params: { merchant_id: merchantId, period }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get top customers by points
   * @param {string} merchantId - Merchant ID
   * @param {number} limit - Number of customers to return (default: 10)
   */
  async getTopCustomers(merchantId, limit = 10) {
    try {
      const response = await this.mtierClient.get("auth/analytics/top-customers", {
        params: { merchant_id: merchantId, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get tier distribution
   * @param {string} merchantId - Merchant ID
   * @param {string} loyaltyId - Optional loyalty program ID filter
   */
  async getTierDistribution(merchantId, loyaltyId = null) {
    try {
      const params = { merchant_id: merchantId };
      if (loyaltyId) {
        params.loyalty_id = loyaltyId;
      }
      const response = await this.mtierClient.get("auth/analytics/tier-distribution", { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get retention curve (cohort-based retention data)
   * @param {string} merchantId - Merchant ID
   * @param {number} cohortMonths - Number of monthly cohorts to analyze (default: 12)
   */
  async getRetentionCurve(merchantId, cohortMonths = 12) {
    try {
      const response = await this.mtierClient.get("auth/analytics/retention-curve", {
        params: { merchant_id: merchantId, cohort_months: cohortMonths }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Export analytics report
   * @param {string} format - 'csv' or 'excel'
   */
  async exportAnalyticsReport(format = 'csv', params = {}) {
    try {
      const response = await this.mtierClient.get(`loyalty/analytics/export`, {
        params: { ...params, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== MEMBER MANAGEMENT (GraphQL) ====================

  /**
   * Execute GraphQL query
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   */
  async graphqlQuery(query, variables = {}) {
    try {
      const response = await this.mtierClient.post("graphql", {
        query,
        variables,
      });
      
      if (response.data.errors) {
        // Check if it's a non-nullable field error with partial data
        const hasNullFieldError = response.data.errors.some(err => 
          err.message.includes('Cannot return null for non-nullable field')
        );
        
        if (hasNullFieldError && response.data.data) {
          // Return partial data if available
          console.warn('GraphQL returned partial data due to null field errors:', response.data.errors);
          return response.data.data;
        }
        
        throw new Error(response.data.errors[0].message);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List all customers with merged data
   */
  async listCustomers() {
    const query = `
      query {
        listMembers {
          id
          customer_id
          first_name
          last_name
          email
          phone_number
          created_at
          customer_custom_fields {
            id
            name
            value
          }
          tiers {
            id
            name
            min_spending_target
            max_spending_target
          }
          points {
            id
            points_value
            expiry_date
            loyalty_id
            loyalty_name
          }
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(query);
      return data.listMembers;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get single customer details with validation
   * @param {string} customerId - Merchant Customer ID (customer_id)
   */
  async getCustomer(customerId) {
    const query = `
      query GetCustomer($id: ID!) {
        getCustomer(id: $id) {
          id
          customer_id
          first_name
          last_name
          email
          created_at
          customer_custom_fields {
            id
            name
            value
          }
          tiers {
            id
            name
            min_spending_target
            max_spending_target
          }
          points {
            id
            points_value
            expiry_date
            loyalty_id
            loyalty_name
          }
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(query, { id: customerId });
      return data.getCustomer;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List customers by tier status
   * @param {string} tierStatus - 'tiered', 'untiered', or 'all'
   */
  async listCustomersByTier(tierStatus = 'all') {
    let query;
    
    if (tierStatus === 'tiered') {
      query = `
        query {
          listTieredCustomers {
            id
            customer_id
            first_name
            last_name
            email
            phone_number
            created_at
            customer_custom_fields {
              id
              name
              value
            }
            tiers {
              id
              name
              min_spending_target
              max_spending_target
            }
            points {
              id
              points_value
              amount
              expiry_date
              loyalty_id
              loyalty_name
            }
          }
        }
      `;
    } else if (tierStatus === 'untiered') {
      query = `
        query {
          listUntieredCustomers {
            id
            customer_id
            first_name
            last_name
            email
            phone_number
            created_at
            customer_custom_fields {
              id
              name
              value
            }
            points {
              id
              points_value
              amount
              expiry_date
              loyalty_id
              loyalty_name
            }
          }
        }
      `;
    } else {
      return this.listCustomers();
    }
    
    try {
      const data = await this.graphqlQuery(query);
      return data.listTieredCustomers || data.listUntieredCustomers;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List loyalty program members
   * @param {string} loyaltyId - Optional loyalty program ID filter
   */
  async listLoyaltyMembers(loyaltyId = null) {
    const query = loyaltyId
      ? `
        query ListLoyaltyCustomers($loyaltyId: ID) {
          listLoyaltyCustomers(loyalty_id: $loyaltyId) {
            id
            customer_id
            first_name
            last_name
            email
            phone_number
            created_at
            tiers {
              id
              name
              min_spending_target
              max_spending_target
            }
            points {
              id
              points_value
              amount
              expiry_date
              loyalty_id
              loyalty_name
            }
          }
        }
      `
      : `
        query {
          listMembers {
            id
            customer_id
            first_name
            last_name
            email
            phone_number
            created_at
            tiers {
              id
              name
              min_spending_target
              max_spending_target
            }
            points {
              id
              points_value
              amount
              expiry_date
              loyalty_id
              loyalty_name
            }
          }
        }
      `;
    
    try {
      const data = await this.graphqlQuery(query, loyaltyId ? { loyaltyId } : {});
      return data.listLoyaltyCustomers || data.listMembers;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Edit customer details
   * @param {string} customerId - Customer ID
   * @param {Object} data - Customer data to update (first_name, last_name, email)
   */
  async editCustomer(customerId, data) {
    const mutation = `
      mutation EditCustomer($id: String!, $data: EditCustomerInput!) {
        editCustomer(id: $id, data: $data) {
          id
          customer_id
          first_name
          last_name
          email
          customer_custom_fields {
            id
            name
            value
          }
        }
      }
    `;
    
    try {
      // Convert customer_id to string as the API expects
      const result = await this.graphqlQuery(mutation, { id: String(customerId), data });
      return result.editCustomer;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Redeem customer loyalty points
   * @param {number} customerId - Customer ID
   * @param {string} loyaltyId - Loyalty program ID
   * @param {number} amount - Points amount to redeem
   */
  async redeemCustomerPoints(customerId, loyaltyId, amount) {
    const mutation = `
      mutation RedeemPoints($customerId: Int!, $loyaltyId: ID!, $amount: Float!) {
        redeemCustomerPoints(
          customer_id: $customerId
          loyalty_id: $loyaltyId
          amount: $amount
        ) {
          id
          customer_id
          first_name
          last_name
          email
          points {
            id
            points_value
            amount
            expiry_date
          }
          tiers {
            id
            name
            min_spending_target
            max_spending_target
          }
        }
      }
    `;
    
    try {
      const result = await this.graphqlQuery(mutation, {
        customerId: parseInt(customerId),
        loyaltyId,
        amount: parseFloat(amount),
      });
      return result.redeemCustomerPoints;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete customer (soft delete with archive)
   * @param {string} customerId - Customer ID
   */
  async deleteCustomer(customerId) {
    const mutation = `
      mutation DeleteCustomer($id: String!) {
        deleteCustomer(id: $id) {
          status
          message
        }
      }
    `;
    
    try {
      const result = await this.graphqlQuery(mutation, { id: String(customerId) });
      return { success: result.deleteCustomer?.status === 'success' || result.deleteCustomer?.status === true };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get multitier transactions for merchant
   * @param {number} merchantId - Merchant ID
   */
  async getTransactions(merchantId) {
    const query = `
      query GetMultitierTransactions($merchant_id: Int!) {
        getMultitierTransactions(merchant_id: $merchant_id) {
          points
          mtier_amount
          total
          tier
          transaction_type
          transactions
          customer
          customer_name
          loyalty_name
          staff
          merchant_id
          transaction_date
        }
      }
    `;
    
    try {
      console.log('Querying transactions with merchant_id:', merchantId, 'parsed as:', parseInt(merchantId));
      const data = await this.graphqlQuery(query, { merchant_id: parseInt(merchantId) });
      console.log('GraphQL response:', data);
      return data.getMultitierTransactions;
    } catch (error) {
      console.error('Error in getTransactions:', error);
      throw this.handleError(error);
    }
  }

  // ==================== DATA UPLOAD ====================

  /**
   * Upload customer activity data file
   * @param {File} file - CSV or Excel file
   * @param {string} activityType - Type of activity being uploaded
   */
  async uploadActivityData(file, activityType) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("activity_type", activityType);

      const response = await this.mtierClient.post("loyalty/data-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get upload template
   * @param {string} type - Template type
   */
  async getUploadTemplate(type = 'transaction') {
    try {
      const response = await this.mtierClient.get(`loyalty/data-upload/template`, {
        params: { type },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get upload history
   */
  async getUploadHistory(params = {}) {
    try {
      const response = await this.mtierClient.get("loyalty/data-upload/history", { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get upload status and details
   */
  async getUploadStatus(uploadId) {
    try {
      const response = await this.mtierClient.get(`loyalty/data-upload/${uploadId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== REWARDS & REDEMPTION ====================

  /**
   * Get available rewards catalog
   */
  async getRewardsCatalog(params = {}) {
    try {
      const response = await this.mtierClient.get("loyalty/rewards", { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get redemption requests/history (UGiftMe orders)
   * @param {string} merchantId - Merchant UUID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.customer_id - Filter by specific customer
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.status - Filter by order status (e.g., "pending", "fulfilled", "completed")
   */
  async getRedemptions(merchantId, params = {}) {
    try {
      const response = await this.mtierClient.get("auth/ugiftme/orders", {
        params: { merchant_id: merchantId, ...params }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Process/approve redemption request
   */
  async processRedemption(redemptionId, action, notes = "") {
    try {
      const response = await this.mtierClient.post(`loyalty/redemptions/${redemptionId}/process`, {
        action, // 'approve' or 'reject'
        notes
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== UGIFTME INTEGRATION ====================

  /**
   * Configure UGiftMe integration for merchant
   * @param {Object} configData - UGiftMe configuration data
   * @param {string} configData.merchant_id - Merchant UUID
   * @param {string} configData.api_key - UGiftMe API key
   * @param {string} configData.currency - Currency code (e.g., "NGN")
   * @param {string[]} configData.allowed_countries - Array of country codes
   * @param {boolean} configData.is_active - Active status
   */
  async configureUGiftMe(configData) {
    try {
      const response = await this.mtierClient.post("auth/ugiftme/config", configData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message?.includes('uuid')) {
        throw new Error('Invalid merchant ID format. Please contact support if this issue persists.');
      }
      if (error.response?.data?.message?.includes('access token')) {
        throw new Error('Authentication failed. Please log out and log in again.');
      }
      throw this.handleError(error);
    }
  }

  /**
   * Get UGiftMe configuration for merchant
   * @param {string} merchantId - Merchant UUID
   */
  async getUGiftMeConfig(merchantId) {
    try {
      if (!merchantId) {
        throw new Error('Merchant ID is required for UGiftMe configuration');
      }
      const response = await this.mtierClient.get("auth/ugiftme/config", {
        params: { merchant_id: merchantId }
      });
      return response.data;
    } catch (error) {
      // Handle 404 - config doesn't exist yet
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Configuration not found',
          data: null
        };
      }
      
      throw this.handleError(error);
    }
  }

  /**
   * Update redemption settings (auto-fulfill orders toggle)
   * @param {string} merchantId - Merchant UUID
   * @param {boolean} autoFulfillOrders - Whether to auto-fulfill orders
   * @returns {Promise<Object>} Updated settings
   */
  async updateRedemptionSettings(merchantId, autoFulfillOrders) {
    try {
      if (!merchantId) {
        throw new Error('Merchant ID is required for updating redemption settings');
      }
      const response = await this.mtierClient.patch("auth/ugiftme/config/settings", {
        merchant_id: merchantId,
        auto_fulfill_orders: autoFulfillOrders
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get UGiftMe products portfolio (full catalog)
   * @param {string} merchantId - Merchant UUID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.category - Filter by category
   * @param {string} params.merchant - Filter by merchant name
   * @param {string} params.search - Search term
   */
  async getUGiftMeProducts(merchantId, params = {}) {
    try {
      const response = await this.mtierClient.get("auth/ugiftme/products", {
        params: { merchant_id: merchantId, ...params }
      });
      console.log('UGiftMe products API response:', response.data);
      if (response.data?.data?.products) {
        console.log('Sample product:', response.data.data.products[0]);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get specific UGiftMe product details
   * @param {string} merchantId - Merchant UUID
   * @param {string} productId - Product ID
   */
  async getUGiftMeProductDetails(merchantId, productId) {
    try {
      const response = await this.mtierClient.get(`auth/ugiftme/products/${productId}`, {
        params: { merchant_id: merchantId }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get available product filters (categories and merchants)
   * @param {string} merchantId - Merchant UUID
   */
  async getUGiftMeProductFilters(merchantId) {
    try {
      const response = await this.mtierClient.get("auth/ugiftme/products/filters", {
        params: { merchant_id: merchantId }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate default curation (auto-curate products based on country)
   * @param {string} merchantId - Merchant UUID
   */
  async generateDefaultCuration(merchantId) {
    try {
      const response = await this.mtierClient.post("auth/ugiftme/curations/generate-default", {
        merchant_id: merchantId
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get curated products (merchant's gift card catalog)
   * @param {string} merchantId - Merchant UUID
   * @param {Object} params - Query parameters
   * @param {boolean} params.is_active - Filter by active status
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   */
  async getCuratedProducts(merchantId, params = {}) {
    try {
      const response = await this.mtierClient.get("auth/ugiftme/curations", {
        params: { merchant_id: merchantId, ...params }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add products to curation manually
   * @param {string} merchantId - Merchant UUID
   * @param {string[]} productIds - Array of product IDs to add
   */
  async addProductsToCuration(merchantId, productIds) {
    try {
      // Backend expects only product_ids array and will fetch product details from UGiftMe API
      const response = await this.mtierClient.post("auth/ugiftme/curations", {
        merchant_id: merchantId,
        product_ids: productIds
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update curation entry (e.g., toggle active/inactive)
   * @param {string} curationId - Curation entry ID
   * @param {string} merchantId - Merchant UUID
   * @param {Object} data - Update data
   * @param {boolean} data.is_active - Active status
   */
  async updateCuration(curationId, merchantId, data) {
    try {
      const response = await this.mtierClient.patch(`auth/ugiftme/curations/${curationId}`, {
        merchant_id: merchantId,
        ...data
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove product from curation
   * @param {string} curationId - Curation entry ID
   * @param {string} merchantId - Merchant UUID
   */
  async removeFromCuration(curationId, merchantId) {
    try {
      const response = await this.mtierClient.delete(`auth/ugiftme/curations/${curationId}`, {
        params: { merchant_id: merchantId }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create gift card order
   * @param {Object} orderData - Order data
   * @param {string} orderData.merchant_id - Merchant UUID
   * @param {string} orderData.customer_id - Customer UUID
   * @param {string} orderData.product_id - Product ID
   * @param {number} orderData.amount - Order amount
   * @param {number} orderData.quantity - Quantity
   */
  async createGiftCardOrder(orderData) {
    try {
      const response = await this.mtierClient.post("auth/ugiftme/orders", orderData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get gift card orders
   * @param {string} merchantId - Merchant UUID
   * @param {Object} params - Query parameters
   * @param {string} params.customer_id - Filter by customer
   * @param {string} params.status - Filter by status (pending, fulfilled, failed)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   */
  async getGiftCardOrders(merchantId, params = {}) {
    try {
      const response = await this.mtierClient.get("auth/ugiftme/orders", {
        params: { merchant_id: merchantId, ...params }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get specific gift card order details
   * @param {string} orderId - Order ID
   * @param {string} merchantId - Merchant UUID
   */
  async getGiftCardOrderDetails(orderId, merchantId) {
    try {
      const response = await this.mtierClient.get(`auth/ugiftme/orders/${orderId}`, {
        params: { merchant_id: merchantId }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Activate/fulfill a gift card order
   * @param {string} orderId - Order ID
   * @param {string} merchantId - Merchant UUID
   */
  async activateGiftCardOrder(orderId, merchantId) {
    try {
      const response = await this.mtierClient.post(`auth/ugiftme/orders/${orderId}/activate`, {
        merchant_id: merchantId
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get UGiftMe wallet balance
   * @param {string} merchantId - Merchant UUID
   */
  async getUGiftMeWalletBalance(merchantId) {
    try {
      console.log('API: Fetching UGiftMe wallet balance for merchant:', merchantId);
      const response = await this.mtierClient.get("auth/ugiftme/wallet", {
        params: { merchant_id: merchantId }
      });
      console.log('API: Wallet balance response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API: Error fetching wallet balance:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get UGiftMe wallet transactions
   * @param {string} merchantId - Merchant UUID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.currency - Filter by currency
   * @param {string} params.from_date - Start date (YYYY-MM-DD)
   * @param {string} params.to_date - End date (YYYY-MM-DD)
   * @param {string} params.type - Transaction type
   */
  async getUGiftMeWalletTransactions(merchantId, params = {}) {
    try {
      const response = await this.mtierClient.get("auth/ugiftme/wallet/transactions", {
        params: { merchant_id: merchantId, ...params }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== LOYALTY PROGRAMS ====================

  /**
   * Get all loyalty programs for the merchant via GraphQL
   * @returns {Promise<Array>} List of loyalty programs with tiers
   */
  async getLoyaltyPrograms() {
    const query = `
      query {
        listLoyalties {
          id
          name
          currency_value
          points
          owner_id
          created_at
          verification
          expiration
          status
          image_url
          tier {
            id
            name
            min_spending_target
            max_spending_target
            magnification
          }
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(query);
      return data.listLoyalties || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get single loyalty program details via GraphQL
   * @param {string} programId - Program ID
   * @returns {Promise<Object>} Program details with tiers
   */
  async getLoyaltyProgram(programId) {
    const query = `
      query GetLoyalty($id: ID!) {
        getLoyalty(id: $id) {
          id
          name
          currency_value
          points
          owner_id
          created_at
          verification
          expiration
          status
          image_url
          tier {
            id
            name
            min_spending_target
            max_spending_target
            magnification
            reward
            is_reward_self
            instruction
            created_at
          }
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(query, { id: programId });
      return data.getLoyalty;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create new loyalty program via GraphQL
   * @param {Object} programData - Program data
   * @param {string} programData.name - Program name
   * @param {string} programData.image_url - Program logo URL
   * @param {number} programData.currency_value - Currency value for points calculation
   * @param {number} programData.points - Points per currency value
   * @param {boolean} programData.verification - Require verification
   * @param {number} programData.expiration - Days until points expire
   * @returns {Promise<Object>} Created program
   */
  async createLoyaltyProgram(programData) {
    const mutation = `
      mutation CreateLoyalty(
        $name: String!
        $image_url: String!
        $currency_value: Int!
        $points: Int!
        $verification: Boolean
        $expiration: Int
      ) {
        createLoyalty(
          name: $name
          image_url: $image_url
          currency_value: $currency_value
          points: $points
          verification: $verification
          expiration: $expiration
        ) {
          id
          name
          image_url
          currency_value
          points
          verification
          expiration
        }
      }
    `;
    
    try {
      const variables = {
        name: programData.name,
        image_url: programData.image_url || 'https://loystar.co/assets/images/logo.png',
        currency_value: parseInt(programData.currency_value) || 100,
        points: parseInt(programData.points_per_currency) || 1,
        verification: programData.verification || false,
        expiration: programData.expiration ? parseInt(programData.expiration) : null,
      };
      
      const data = await this.graphqlQuery(mutation, variables);
      return data.createLoyalty;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update loyalty program via GraphQL
   * @param {string} programId - Program ID
   * @param {Object} programData - Updated program data
   * @returns {Promise<Object>} Updated program
   */
  async updateLoyaltyProgram(programId, programData) {
    const mutation = `
      mutation EditLoyalty($id: ID!, $data: EditLoyaltyInput!) {
        editLoyalty(id: $id, data: $data) {
          id
          name
          image_url
          currency_value
          points
          verification
          expiration
        }
      }
    `;
    
    try {
      const data = {
        name: programData.name,
        image_url: programData.image_url,
        currency_value: programData.currency_value ? parseInt(programData.currency_value) : undefined,
        points: programData.points_per_currency ? parseInt(programData.points_per_currency) : undefined,
        verification: programData.verification,
        expiration: programData.expiration ? parseInt(programData.expiration) : undefined,
      };
      
      // Remove undefined values
      Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);
      
      const result = await this.graphqlQuery(mutation, { id: programId, data });
      return result.editLoyalty;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete loyalty program via GraphQL
   * @param {string} programId - Program ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteLoyaltyProgram(programId) {
    const mutation = `
      mutation DeleteLoyalty($id: ID!) {
        deleteLoyalty(id: $id) {
          status
          message
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(mutation, { id: programId });
      return data.deleteLoyalty;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Activate loyalty program via GraphQL
   * @param {string} programId - Program ID
   * @returns {Promise<Object>} Activation result
   */
  async activateLoyaltyProgram(programId) {
    const mutation = `
      mutation ActivateLoyalty($id: ID!) {
        activateLoyalty(id: $id) {
          id
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(mutation, { id: programId });
      return data.activateLoyalty;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Deactivate loyalty program via GraphQL
   * @param {string} programId - Program ID
   * @returns {Promise<Object>} Deactivation result
   */
  async deactivateLoyaltyProgram(programId) {
    const mutation = `
      mutation DeactivateLoyalty($id: ID!) {
        deActivateLoyalty(id: $id) {
          id
          name
          image_url
          currency_value
          status
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(mutation, { id: programId });
      return data.deActivateLoyalty;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List all tiers for a loyalty program via GraphQL
   * @param {string} programId - Program ID (loyalty_id)
   * @returns {Promise<Array>} List of tiers with customers
   */
  async listLoyaltyTiers(programId) {
    const query = `
      query listTiers($loyalty_id: ID!) {
        listTiers(loyalty_id: $loyalty_id) {
          id
          name
          min_spending_target
          max_spending_target
          is_reward_self
          reward
          instruction
          magnification
          range
          tierPosition
          created_at
          customers {
            id
            customer_id
            first_name
            last_name
            email
            points {
              id
              points_value
              expiry_date
              loyalty_name
              loyalty_id
            }
          }
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(query, { loyalty_id: programId });
      return data.listTiers || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create tier for a loyalty program via GraphQL
   * @param {string} programId - Program ID (loyalty_id)
   * @param {Object} tierData - Tier data
   * @param {string} tierData.name - Tier name
   * @param {number} tierData.min_spending - Minimum spending target
   * @param {number} tierData.max_spending - Maximum spending target
   * @param {number} tierData.points_multiplier - Points magnification/multiplier
   * @param {string} tierData.reward - Reward description
   * @param {string} tierData.instruction - Instructions for tier
   * @param {boolean} tierData.is_reward_self - Is reward self-redeemable
   * @returns {Promise<Object>} Created tier
   */
  async createLoyaltyTier(programId, tierData) {
    const mutation = `
      mutation CreateTier(
        $name: String!
        $min_spending_target: Int!
        $max_spending_target: Int!
        $is_reward_self: Boolean
        $reward: String
        $instruction: String
        $loyalty_id: ID!
        $partners_id: ID
        $magnification: Int
      ) {
        createTier(
          name: $name
          min_spending_target: $min_spending_target
          max_spending_target: $max_spending_target
          is_reward_self: $is_reward_self
          magnification: $magnification
          reward: $reward
          instruction: $instruction
          loyalty_id: $loyalty_id
          partners_id: $partners_id
        ) {
          id
          name
          min_spending_target
          max_spending_target
          is_reward_self
          range
          reward
          magnification
          created_at
          instruction
        }
      }
    `;
    
    try {
      const variables = {
        name: tierData.name,
        min_spending_target: parseInt(tierData.min_spending || tierData.min_spending_target),
        max_spending_target: parseInt(tierData.max_spending || tierData.max_spending_target),
        magnification: parseInt(tierData.points_multiplier || tierData.magnification || 1),
        reward: tierData.reward || tierData.benefits || '',
        instruction: tierData.instruction || '',
        is_reward_self: tierData.is_reward_self || false,
        loyalty_id: programId,
        partners_id: tierData.partners_id || null,
      };
      
      const data = await this.graphqlQuery(mutation, variables);
      return data.createTier;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update loyalty tier via GraphQL
   * @param {string} programId - Program ID (not used in GraphQL but kept for consistency)
   * @param {string} tierId - Tier ID
   * @param {Object} tierData - Updated tier data
   * @returns {Promise<Object>} Updated tier
   */
  async updateLoyaltyTier(programId, tierId, tierData) {
    console.log("updateLoyaltyTier - programId:", programId);
    console.log("updateLoyaltyTier - tierId:", tierId);
    console.log("updateLoyaltyTier - tierData:", tierData);

    const mutation = `
      mutation EditTier($id: ID!, $data: EditTierInput!) {
        editTier(id: $id, data: $data) {
          id
          name
          min_spending_target
          max_spending_target
          reward
          magnification
          range
          is_reward_self
        }
      }
    `;
    
    try {
      const data = {
        name: tierData.name,
        min_spending_target: tierData.min_spending !== undefined && tierData.min_spending !== null 
          ? parseInt(tierData.min_spending) 
          : undefined,
        max_spending_target: tierData.max_spending !== undefined && tierData.max_spending !== null 
          ? parseInt(tierData.max_spending) 
          : undefined,
        magnification: tierData.points_multiplier !== undefined && tierData.points_multiplier !== null 
          ? parseInt(tierData.points_multiplier) 
          : undefined,
        reward: tierData.reward || tierData.benefits,
        instruction: tierData.instruction,
        is_reward_self: tierData.is_reward_self !== undefined ? tierData.is_reward_self : true, // Default to true (self-sponsored)
      };
      
      // Remove undefined values (but keep is_reward_self since it's required)
      Object.keys(data).forEach(key => {
        if (key !== 'is_reward_self' && data[key] === undefined) {
          delete data[key];
        }
      });
      
      console.log("updateLoyaltyTier - mutation data:", data);
      
      const result = await this.graphqlQuery(mutation, { id: tierId, data });
      console.log("updateLoyaltyTier - result:", result);
      return result.editTier;
    } catch (error) {
      console.error("updateLoyaltyTier - error:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete loyalty tier via GraphQL
   * @param {string} programId - Program ID (not used in GraphQL but kept for consistency)
   * @param {string} tierId - Tier ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteLoyaltyTier(programId, tierId) {
    const mutation = `
      mutation DeleteTier($id: ID!) {
        deleteTier(id: $id) {
          status
          message
        }
      }
    `;
    
    try {
      const data = await this.graphqlQuery(mutation, { id: tierId });
      return data.deleteTier;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== CUSTOMER ENROLLMENT ====================

  /**
   * Create merchant customer via REST API
   * @param {Object} customerData - Customer information
   * @returns {Promise<Object>} Created customer with merchant customer_id
   */
  async createMerchantCustomer(customerData) {
    try {
      console.log('API: Creating merchant customer');
      console.log('API: Request data:', customerData);
      
      const response = await this.apiClient.post('/add_user_direct', {
        data: {
          first_name: customerData.first_name,
          last_name: customerData.last_name,
          email: customerData.email,
          phone_number: customerData.phone_number,
          sex: customerData.sex || '-',
          custom_data: customerData.custom_data || [],
        }
      });

      console.log('API: Response from add_user_direct:', response.data);

      return {
        id: response.data.id,
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        email: response.data.email,
        phone_number: response.data.phone_number,
      };
    } catch (error) {
      console.error('API: Error creating merchant customer:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Enroll customer in loyalty program via GraphQL
   * @param {Object} enrollmentData - Enrollment details
   * @returns {Promise<Object>} Enrolled customer
   */
  async enrollCustomerInLoyalty(enrollmentData) {
    const mutation = `
      mutation EnrollCustomer($loyalty_id: ID!, $customer: CustomerInput!, $amount_spent: Float!) {
        enrollCustomerWithAmountSpent(
          loyalty_id: $loyalty_id
          customer: $customer
          amount_spent: $amount_spent
        ) {
          id
          customer_id
          first_name
          last_name
          email
        }
      }
    `;

    try {
      const variables = {
        loyalty_id: enrollmentData.loyalty_id,
        customer: {
          customer_id: enrollmentData.customer_id,
          first_name: enrollmentData.first_name,
          last_name: enrollmentData.last_name,
          email: enrollmentData.email,
          phone_number: enrollmentData.phone_number,
        },
        amount_spent: parseFloat(enrollmentData.amount_spent || 0),
      };
      
      console.log('API: Enrolling customer in loyalty program');
      console.log('API: GraphQL variables:', variables);
      
      const result = await this.graphqlQuery(mutation, variables);
      
      console.log('API: Enrollment result:', result);

      return result.enrollCustomerWithAmountSpent;
    } catch (error) {
      console.error('API: Error enrolling customer:', error);
      throw this.handleError(error);
    }
  }

  // ==================== UTILITY METHODS ====================

  handleError(error) {
    if (error.response) {
      // Server responded with error
      return {
        message: error.response.data.message || "An error occurred",
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: "No response from server. Please check your connection.",
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || "An unexpected error occurred",
        status: -1,
      };
    }
  }
}

export default new EnterpriseLoyaltyAPI();
